import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, Alert, CircularProgress,
  Grid, Tab, Tabs, IconButton, Tooltip, Collapse, FormControl,
  InputLabel, Select, MenuItem, LinearProgress,
} from '@mui/material';
import {
  Refresh, Webhook, CheckCircle, Error as ErrorIcon, Schedule,
  ExpandMore, ExpandLess, ContentCopy, FilterList, Delete, DeleteSweep,
} from '@mui/icons-material';
import axios from 'axios';

const IST_FMT = {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
};
function toIST(d) {
  if (!d) return '-';
  let s = String(d);
  if (!s.endsWith('Z') && !s.includes('+') && !s.includes('-', 10)) s += 'Z';
  return new Date(s).toLocaleString('en-IN', IST_FMT);
}

const STATUS_CHIP = {
  Processed: { color: 'success', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  processed: { color: 'success', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  Failed:    { color: 'error',   icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
  failed:    { color: 'error',   icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
  Skipped:   { color: 'default', icon: <Schedule sx={{ fontSize: 14 }} /> },
  skipped:   { color: 'default', icon: <Schedule sx={{ fontSize: 14 }} /> },
  received:  { color: 'warning', icon: <Schedule sx={{ fontSize: 14 }} /> },
};

export default function WebhookDashboard({ apiUrl }) {
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [executions, setExecutions] = useState([]);
  const [execTotal, setExecTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedLog, setExpandedLog] = useState(null);
  const [expandedExec, setExpandedExec] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [copied, setCopied] = useState('');

  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, logsRes, execRes] = await Promise.all([
        axios.get(`${apiUrl}/webhooks/stats`, { headers }),
        axios.get(`${apiUrl}/webhooks/logs`, { headers, params: { limit: 100, status: statusFilter || undefined } }),
        axios.get(`${apiUrl}/webhooks/executions`, { headers, params: { limit: 100 } }),
      ]);
      setStats(statsRes.data);
      setLogs(logsRes.data.logs || []);
      setLogsTotal(logsRes.data.total || 0);
      setExecutions(execRes.data.executions || []);
      setExecTotal(execRes.data.total || 0);
    } catch (err) {
      setError('Failed to load webhook data');
    } finally { setLoading(false); }
  }, [apiUrl, statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleDeleteLog = async (logId, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this webhook log?')) return;
    try {
      await axios.delete(`${apiUrl}/webhooks/logs/${logId}`, { headers });
      fetchAll();
    } catch (err) {
      setError('Failed to delete log');
    }
  };

  const handleDeleteAllLogs = async () => {
    if (!window.confirm('Delete ALL webhook logs? This cannot be undone.')) return;
    try {
      await axios.delete(`${apiUrl}/webhooks/logs`, { headers });
      fetchAll();
    } catch (err) {
      setError('Failed to delete logs');
    }
  };

  const webhookUrl = `${window.location.origin}/api/webhooks/tradingview`;

  return (
    <Box data-testid="webhook-dashboard">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Webhook Dashboard</Typography>
          <Typography variant="caption" color="text.secondary">Monitor TradingView signals, logs & execution history</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" size="small" color="error" startIcon={<DeleteSweep />}
            onClick={handleDeleteAllLogs} disabled={loading || logs.length === 0}
            data-testid="delete-all-logs-btn">Clear All</Button>
          <Button variant="outlined" size="small" startIcon={loading ? <CircularProgress size={14} /> : <Refresh />}
            onClick={fetchAll} disabled={loading} data-testid="refresh-webhooks-btn">Refresh</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Webhook URL */}
      <Paper sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'info.main', borderRadius: 2 }} variant="outlined">
        <Box display="flex" alignItems="center" gap={1}>
          <Webhook color="info" />
          <Typography variant="subtitle2" color="info.main">Webhook URL</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1} mt={1}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 1.5, py: 0.5, borderRadius: 1, flexGrow: 1, wordBreak: 'break-all' }}
            data-testid="webhook-url">{webhookUrl}</Typography>
          <Tooltip title={copied === 'url' ? 'Copied!' : 'Copy URL'}>
            <IconButton size="small" onClick={() => copyToClipboard(webhookUrl, 'url')} data-testid="copy-webhook-url">
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3} md={1.5}>
            <Paper sx={{ p: 1.5, textAlign: 'center' }} variant="outlined">
              <Typography variant="caption" color="text.secondary">Total</Typography>
              <Typography variant="h5" fontWeight={700} data-testid="stat-total">{stats.total_requests}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <Paper sx={{ p: 1.5, textAlign: 'center' }} variant="outlined">
              <Typography variant="caption" color="text.secondary">Processed</Typography>
              <Typography variant="h5" fontWeight={700} color="success.main" data-testid="stat-processed">{stats.processed}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <Paper sx={{ p: 1.5, textAlign: 'center' }} variant="outlined">
              <Typography variant="caption" color="text.secondary">Failed</Typography>
              <Typography variant="h5" fontWeight={700} color="error.main" data-testid="stat-failed">{stats.failed}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <Paper sx={{ p: 1.5, textAlign: 'center' }} variant="outlined">
              <Typography variant="caption" color="text.secondary">Pending</Typography>
              <Typography variant="h5" fontWeight={700} color="warning.main">{stats.pending}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <Paper sx={{ p: 1.5, textAlign: 'center' }} variant="outlined">
              <Typography variant="caption" color="text.secondary">Skipped</Typography>
              <Typography variant="h5" fontWeight={700} color="text.secondary" data-testid="stat-skipped">{stats.skipped || 0}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <Paper sx={{ p: 1.5, textAlign: 'center' }} variant="outlined">
              <Typography variant="caption" color="text.secondary">Success %</Typography>
              <Typography variant="h5" fontWeight={700} color={stats.success_rate >= 80 ? 'success.main' : 'warning.main'}>
                {stats.success_rate}%
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <Paper sx={{ p: 1.5, textAlign: 'center' }} variant="outlined">
              <Typography variant="caption" color="text.secondary">Last 24h</Typography>
              <Typography variant="h5" fontWeight={700} color="info.main">{stats.last_24h}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <Paper sx={{ p: 1.5, textAlign: 'center' }} variant="outlined">
              <Typography variant="caption" color="text.secondary">Strategies</Typography>
              <Typography variant="h5" fontWeight={700}>{stats.unique_strategies}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <Paper sx={{ p: 1.5, textAlign: 'center' }} variant="outlined">
              <Typography variant="caption" color="text.secondary">Symbols</Typography>
              <Typography variant="h5" fontWeight={700}>{stats.unique_symbols}</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }} variant="outlined">
        <Tabs value={tab} onChange={(_, v) => setTab(v)} data-testid="webhook-tabs">
          <Tab label={`Request Log (${logsTotal})`} data-testid="tab-request-log" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label={`Execution History (${execTotal})`} data-testid="tab-execution-history" sx={{ textTransform: 'none', fontWeight: 600 }} />
        </Tabs>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Tab 0: Request Log */}
      {tab === 0 && (
        <Box>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <FilterList color="action" />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-log-status">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="processed">Processed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="skipped">Skipped</MenuItem>
                <MenuItem value="received">Pending</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {logs.length === 0 && !loading && (
            <Alert severity="info">No webhook requests logged yet. Send a POST to the webhook URL above to get started.</Alert>
          )}

          {logs.length > 0 && (
            <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 32 }}></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Time (IST)</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Strategy</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Symbol</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Error</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Processed At</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 40 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => {
                    const chipCfg = STATUS_CHIP[log.status] || STATUS_CHIP.received;
                    const isExpanded = expandedLog === log.id;
                    return (
                      <React.Fragment key={log.id}>
                        <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                          data-testid={`log-row-${log.id}`}>
                          <TableCell>
                            <IconButton size="small">{isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}</IconButton>
                          </TableCell>
                          <TableCell><Typography variant="caption" noWrap>{toIST(log.received_at)}</Typography></TableCell>
                          <TableCell>
                            <Chip icon={chipCfg.icon} label={log.status} size="small" color={chipCfg.color}
                              sx={{ fontSize: '0.7rem', height: 24 }} data-testid={`log-status-${log.id}`} />
                          </TableCell>
                          <TableCell><Chip label={log.source || '-'} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} /></TableCell>
                          <TableCell><Typography variant="body2" fontWeight={600}>{log.strategy_name || '-'}</Typography></TableCell>
                          <TableCell><Typography variant="body2" fontWeight={600}>{log.symbol || '-'}</Typography></TableCell>
                          <TableCell>
                            {log.action ? (
                              <Chip label={log.action.toUpperCase()} size="small"
                                color={log.action.toLowerCase() === 'buy' ? 'success' : log.action.toLowerCase() === 'sell' ? 'error' : 'default'}
                                sx={{ fontSize: '0.65rem', height: 20 }} />
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            {log.error_message ? (
                              <Tooltip title={log.error_message}>
                                <Typography variant="caption" color="error.main" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                                  {log.error_message}
                                </Typography>
                              </Tooltip>
                            ) : '-'}
                          </TableCell>
                          <TableCell><Typography variant="caption" noWrap>{toIST(log.processed_at)}</Typography></TableCell>
                          <TableCell>
                            <IconButton size="small" color="error" onClick={(e) => handleDeleteLog(log.id, e)}
                              data-testid={`delete-log-${log.id}`}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={10} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                            <Collapse in={isExpanded}>
                              <Box sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Raw Payload</Typography>
                                <Box sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 200, overflow: 'auto' }}
                                  data-testid={`log-payload-${log.id}`}>
                                  {(() => {
                                    try { return JSON.stringify(JSON.parse(log.raw_payload), null, 2); }
                                    catch { return log.raw_payload; }
                                  })()}
                                </Box>
                                <Box display="flex" gap={2} mt={1}>
                                  <Typography variant="caption" color="text.secondary">ID: {log.id}</Typography>
                                  <Tooltip title={copied === log.id ? 'Copied!' : 'Copy payload'}>
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); copyToClipboard(log.raw_payload, log.id); }}>
                                      <ContentCopy sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Tab 1: Execution History */}
      {tab === 1 && (
        <Box>
          {executions.length === 0 && !loading && (
            <Alert severity="info">No executed webhook trades yet. Process a valid webhook signal to see execution history.</Alert>
          )}

          {executions.length > 0 && (
            <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 32 }}></TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Received (IST)</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Strategy</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Symbol</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Orders</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Processed (IST)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {executions.map((exec) => {
                    const isExpanded = expandedExec === exec.id;
                    return (
                      <React.Fragment key={exec.id}>
                        <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpandedExec(isExpanded ? null : exec.id)}
                          data-testid={`exec-row-${exec.id}`}>
                          <TableCell>
                            <IconButton size="small">{isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}</IconButton>
                          </TableCell>
                          <TableCell><Typography variant="caption" noWrap>{toIST(exec.received_at)}</Typography></TableCell>
                          <TableCell><Typography variant="body2" fontWeight={600}>{exec.strategy_name || '-'}</Typography></TableCell>
                          <TableCell><Typography variant="body2" fontWeight={600}>{exec.symbol || '-'}</Typography></TableCell>
                          <TableCell>
                            <Chip label={(exec.action || '').toUpperCase()} size="small"
                              color={exec.action?.toLowerCase() === 'buy' ? 'success' : exec.action?.toLowerCase() === 'sell' ? 'error' : 'default'}
                              sx={{ fontSize: '0.65rem', height: 20 }} />
                          </TableCell>
                          <TableCell><Chip label={exec.source} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} /></TableCell>
                          <TableCell>
                            <Chip label="Processed" size="small" color="success" sx={{ fontSize: '0.65rem', height: 20 }} />
                          </TableCell>
                          <TableCell><Typography variant="caption" noWrap>{toIST(exec.processed_at)}</Typography></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={8} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                            <Collapse in={isExpanded}>
                              <Box sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Raw Payload</Typography>
                                <Box sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 200, overflow: 'auto' }}>
                                  {(() => {
                                    try { return JSON.stringify(JSON.parse(exec.raw_payload), null, 2); }
                                    catch { return exec.raw_payload; }
                                  })()}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>ID: {exec.id}</Typography>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
}
